/**
 * What a screen shows when it breaks, instead of a blank page.
 *
 * Without this, one bad row in one list unmounts the whole app and the person
 * is left looking at white. With it, the break is kept to the screen it
 * happened on: the sidebar still works, and moving to another page clears it
 * (`resetKey` is the path).
 *
 * One break gets its own words. After a deploy, a tab that was open before it
 * asks for a code chunk that no longer exists; a reload fetches the new one,
 * and that is all the person needs to be told.
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** When this changes, a broken screen gets another try. */
  resetKey?: string;
  /** Fill the viewport, for the boundary around everything. */
  full?: boolean;
}

interface State {
  error: Error | null;
}

function isStaleBuild(error: Error): boolean {
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(
    error.message
  );
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('A screen broke:', error, info.componentStack);
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error !== null && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const stale = isStaleBuild(error);

    return (
      <div
        role="alert"
        className={
          this.props.full === true
            ? 'flex min-h-dvh items-center justify-center bg-page p-6'
            : 'flex min-h-[60vh] items-center justify-center p-6'
        }
      >
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-brand-500/10 text-brand-500">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-display text-lg font-semibold text-canvas-fg">
            {stale ? 'Hapi has been updated' : 'This page hit a problem'}
          </h1>
          <p className="mt-2 text-sm text-shell-text">
            {stale
              ? 'This tab is still running the old version. Reload to get the new one — nothing you saved is lost.'
              : 'Nothing you saved is lost. Reload to try again, or pick another page from the menu.'}
          </p>
          {!stale && (
            <p className="mt-3 break-words font-mono text-xs text-shell-text opacity-70">{error.message}</p>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </button>
        </div>
      </div>
    );
  }
}
